import { TextInput } from 'lego-on-react';
import React from 'react';

import {
    lengthConfig,
    validateActivationPhrase,
    validateDescription,
    validateDeveloperName,
    validateEmail,
    validateName,
    validateNote,
    validateRsyPlatformId,
    validateStructuredExample,
    validateURL,
} from '@paskills/validation';
import i18n from '@yandex-int/i18n';
import { createUpdateError } from 'client/utils/errorMessage';
import * as keyset from 'i18n/AliceSkillForm';
import block from 'propmods';

import { updateDraft } from '../../../api';
import { WithSnapshot, withSnapshot } from '../../../context/snapshotContext';
import formContainer from '../../../hocs/form-container';
import { FormErrors, FormState, InjectedFormProps } from '../../../hocs/types';
import { routes } from '../../../lib/routes';
import { Example } from '../../../model/common';
import { AliceSkillSettings, SkillAccess } from '../../../model/settings';
import { AliceSkill, Channel } from '../../../model/skill';
import { AliceSkillFormSettings } from '../../../types';
import { isPrivateSkillsEnabled } from '../../../utils';
import { mapSkillSettingsToValues, mapValuesToSkillSettings } from '../../../utils/settings/alice-skill';
import { removeDuplicates } from '../../../utils/settings/common';
import { AppMetricaSettings } from '../../AppMetricaSettings/AppMetricaSettings';
import BackendTypeField from '../../BackendTypeField/BackendTypeField';
import ExamplesBuilder from '../../ExamplesBuilder/ExamplesBuilder';
import EmailField from '../../Form/EmailField/EmailField';
import FieldList, { renderTextInput } from '../../Form/FieldList';
import { CheckboxField, Field, Header } from '../../Form/Form';
import TextArea from '../../Form/TextArea';
import TextInputWithCommonness from '../../Form/TextInputWithCommonness/TextInputWithCommonness';
import HostsSelect from '../../Hosts/HostsSelect';
import InterfaceSelector from '../../InterfaceSelector/InterfaceSelector';
import { Select } from '../../Select/Select';
import StaticAudio from '../../StaticAudio/StaticAudio';
import Uploader from '../../Uploader/Uploader';
import Submit from '../Submit';

import {
    activationPhrasesCaption,
    brandVerificationWebsiteCaptionSkill,
    rsyFieldDescription,
    skillNameCaption,
    stateFlagDescription,
    structuredExamplesCaption,
} from './descriptions';
import { skillLabels as l } from './labels';
import { SkillAccessField } from './SkillAccessField';
import * as utils from './utils';

import './AliceSkillForm.scss';

const t = i18n(keyset);

interface OuterProps extends WithSnapshot {
    skillUseZora: boolean;
    logoUrl: string;
    logoColor: string;
    disabled: boolean;
    displayEmailVerificationError: boolean;
    displayBrandVerificationError?: boolean;
    brandIsVerified?: boolean;
    isDraft: boolean;
    settings: AliceSkillSettings;
    isAccountLinkingTemplate: boolean;
    skill: AliceSkill;
}

type Props = InjectedFormProps<AliceSkillFormSettings, OuterProps>;

const b = block('AliceSkillForm');

const AliceForm: React.SFC<Props> = (props) => {
    const {
        values,
        isSubmitting,
        errors,
        onChange,
        onCheckboxChange,
        onUploaderChange,
        setStateAndValidate,
        isValid,
        touched,
        isNonValidationError,
        snapshot,
        skill,
    } = props;

    const isPrivate = values.skillAccess === SkillAccess.Private;

    const { voices } = snapshot;
    const categories =
        snapshot.categories[0].type === 'smart_home' ? snapshot.categories.slice(1) : snapshot.categories;

    const isEmailSent = props.displayEmailVerificationError && !touched.email;
    const submitMessage = isNonValidationError
        ? t('error-occurred')
        : isValid
          ? ''
          : errors.common || t('errors-were-found');
    const submitSeverity = !isValid || isNonValidationError ? 'error' : 'info';

    const voice = values.voice && voices.find((x) => x.name === values.voice);
    const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, skill.developerType);

    const displayExamplesBuilder = values.activationPhrases.filter(Boolean).length > 0;

    const displayAppMetricaSettings =
        Boolean(sessionStorage.getItem('__display-app-metrica-settings')) ||
        Boolean(__appConfig.app.displayAppMetricaSettings) ||
        Boolean(__appConfig.user.featureFlags.allowUseAppMetrica);

    const displayStateFlag =
        Boolean(__appConfig.user.featureFlags.stateStorage) || Boolean(__appConfig.app.displayStateStorageField);

    const displayRsyField =
        Boolean(sessionStorage.getItem('__display-rsy-field')) || Boolean(__appConfig.app.displayRsyField);

    const hideCommonness = props.brandIsVerified || isPrivate;

    const displaySkillAccess = isPrivateSkillsEnabled();

    return (
        <div {...b()}>
            <Header>{t('header-main')}</Header>
            <Field name="name" required label={l.name} error={errors.name} caption={skillNameCaption}>
                <TextInputWithCommonness
                    value={values.name}
                    disabled={props.disabled}
                    fieldLabel={l.name}
                    hideCommonness={hideCommonness}
                    onChange={(name) => {
                        const newActivationPhrases = [name, ...values.activationPhrases.slice(1)];
                        const changedValues: AliceSkillFormSettings = {
                            ...values,
                            name,
                            activationPhrases: newActivationPhrases,
                            structuredExamples: utils.buildStructuredExamples(
                                values.structuredExamples,
                                name,
                                newActivationPhrases,
                            ),
                        };

                        const changedErorrs: FormErrors<AliceSkillFormSettings> = {
                            ...errors,
                            structuredExamples: changedValues.structuredExamples.map((value, i) => {
                                const oldError = errors.structuredExamples ? errors.structuredExamples[i] : '';

                                return validateStructuredExample(value, values.activationPhrases) || oldError;
                            }),
                        };

                        setStateAndValidate(
                            {
                                values: changedValues,
                                errors: changedErorrs,
                            },
                            ['name', 'activationPhrases'],
                        );
                    }}
                />
            </Field>
            <FieldList
                listType="manual"
                renderInput={(p, i) => (
                    <TextInputWithCommonness
                        {...p}
                        fieldLabel="Активационное имя"
                        hideCommonness={hideCommonness}
                        spinnerGapRight
                        disabled={i === 0 || p.disabled}
                    />
                )}
                defaultValue=""
                name="activationPhrases"
                disabled={props.disabled}
                errors={errors.activationPhrases}
                label={l.activationPhrases}
                minCount={1}
                maxCount={validateAsAdmin ? Infinity : 4}
                disableFirstRemoveButton
                onChange={(activationPhrases) => {
                    const changedValues: AliceSkillFormSettings = {
                        ...values,
                        activationPhrases,
                        structuredExamples: utils.buildStructuredExamples(
                            values.structuredExamples,
                            values.name,
                            activationPhrases,
                        ),
                    };

                    const changedErorrs: FormErrors<AliceSkillFormSettings> = {
                        ...errors,
                        structuredExamples: changedValues.structuredExamples.map((value, i) => {
                            const oldError = errors.structuredExamples ? errors.structuredExamples[i] : '';

                            return validateStructuredExample(value, activationPhrases) || oldError;
                        }),
                    };

                    setStateAndValidate(
                        {
                            values: changedValues,
                            errors: changedErorrs,
                        },
                        ['activationPhrases'],
                    );
                }}
                values={values.activationPhrases}
                caption={activationPhrasesCaption}
                documentationUrl={routes.documentation.aliceSkills.generalSettings}
            />
            <BackendTypeField
                skill={skill}
                onBackendTypeChange={onChange('backendType')}
                onFunctionIdChange={onChange('functionId')}
                onWebhookChange={onChange('uri')}
                errors={errors}
                values={values}
                disabled={props.disabled}
            />
            <Field label={l.voice} required name="voice">
                <div {...b('Voice__Field')}>
                    <Select
                        label={l.voice}
                        namespace="voice"
                        placeholder="Выбрать голос"
                        type="radio"
                        value={values.voice}
                        onChange={(value) => {
                            onChange('voice')(value);
                        }}
                        disabled={props.disabled}
                        options={voices.map(({ name, title }) => ({ value: name, text: title }))}
                    />
                    {voice && voice.samples && voice.samples.length ? (
                        <StaticAudio key={voice.name} sources={voice.samples} />
                    ) : null}
                </div>
            </Field>
            {displayStateFlag && (
                <>
                    <CheckboxField
                        label={l.useStateStorage}
                        name="useStateStorage"
                        checked={values.useStateStorage}
                        disabled={props.disabled}
                        onChange={onCheckboxChange('useStateStorage')}
                        description={stateFlagDescription}
                    >
                        {t('storage-option')}
                    </CheckboxField>
                </>
            )}
            {displaySkillAccess && (
                <SkillAccessField
                    skillAccess={values.skillAccess}
                    setSkillAccess={onChange('skillAccess')}
                    disabled={props.disabled}
                    channel={Channel.AliceSkill}
                />
            )}
            <InterfaceSelector
                label={l.requiredInterfaces}
                disabled={props.disabled}
                values={values.requiredInterfaces}
                onChange={onChange('requiredInterfaces')}
            />
            {__appConfig.user.isAdmin && (
                <>
                    <FieldList
                        disabled={props.disabled}
                        errors={errors.exactSurfaces}
                        label={l.exactSurfaces}
                        maxCount={Infinity}
                        minCount={1}
                        onChange={onChange('exactSurfaces')}
                        values={values.exactSurfaces}
                        listType="auto"
                        renderInput={renderTextInput}
                    />
                    <FieldList
                        disabled={props.disabled}
                        errors={errors.surfaceWhitelist}
                        label={l.surfaceWhitelist}
                        maxCount={Infinity}
                        minCount={1}
                        onChange={onChange('surfaceWhitelist')}
                        values={values.surfaceWhitelist}
                        listType="auto"
                        renderInput={renderTextInput}
                    />
                    <FieldList
                        disabled={props.disabled}
                        errors={errors.surfaceBlacklist}
                        label={l.surfaceBlacklist}
                        maxCount={Infinity}
                        minCount={1}
                        onChange={onChange('surfaceBlacklist')}
                        values={values.surfaceBlacklist}
                        listType="auto"
                        renderInput={renderTextInput}
                    />
                </>
            )}
            <Header title={t('header-publication')} subtitle={t('header-publication-subtitle')} />
            {displayExamplesBuilder && (
                <ExamplesBuilder
                    required
                    disabled={props.disabled}
                    errors={errors.structuredExamples}
                    label={l.structuredExamples}
                    values={values.structuredExamples}
                    activationPhrasesOptions={removeDuplicates(
                        [values.name, ...values.activationPhrases].filter(Boolean),
                    )}
                    onChange={onChange('structuredExamples')}
                    caption={structuredExamplesCaption}
                    documentationUrl={routes.documentation.aliceSkills.structuredExamples}
                />
            )}
            {!displaySkillAccess && (
                <CheckboxField
                    label={l.hideInStore}
                    name="hideInStore"
                    checked={values.hideInStore}
                    disabled={props.disabled}
                    onChange={onCheckboxChange('hideInStore')}
                >
                    Не показывать в каталоге
                </CheckboxField>
            )}
            <Field required name="developerName" label={l.developerName} error={errors.developerName}>
                <TextInput
                    theme="normal"
                    size="s"
                    text={values.developerName}
                    onChange={onChange('developerName')}
                    disabled={props.disabled}
                />
            </Field>
            <EmailField
                label={l.email}
                error={errors.email}
                verificationError={props.displayEmailVerificationError ? 'Не подтверждено' : undefined}
                value={values.email}
                onChange={onChange('email')}
                disabled={props.disabled}
                isEmailSent={isEmailSent}
                skillId={skill.id}
            />
            <HostsSelect
                id="brandVerificationWebsite"
                onHostChange={onChange('brandVerificationWebsite')}
                disabled={props.disabled}
                verificationError={props.displayBrandVerificationError ? 'Не проверено' : undefined}
                name="brandVerificationWebsite"
                label={l.brandVerificationWebsite}
                caption={brandVerificationWebsiteCaptionSkill}
                documentationUrl={routes.documentation.aliceSkills.publicationSettings}
                error={errors.brandVerificationWebsite}
                text={values.brandVerificationWebsite}
            />
            <Field label={l.category} required name="category">
                <Select
                    label={l.category}
                    namespace="category"
                    placeholder="Выбрать категорию"
                    type="radiocheck"
                    value={values.category}
                    onChange={(value) => {
                        onChange('category')(value);
                    }}
                    disabled={props.disabled}
                    options={categories.map(({ type, title }) => ({ value: type, text: title }))}
                />
            </Field>
            <Field required name="description" label={l.description} error={errors.description}>
                <TextArea
                    rows={4}
                    limit={lengthConfig.description}
                    text={values.description}
                    onChange={onChange('description')}
                    disabled={props.disabled}
                />
            </Field>
            {props.isDraft && (
                <Field
                    name="noteForModerator"
                    label={l.noteForModerator}
                    error={errors.noteForModerator}
                    caption={t('moderator-notes-description')}
                >
                    <TextArea
                        rows={4}
                        limit={lengthConfig.noteForModerator}
                        text={values.noteForModerator}
                        onChange={onChange('noteForModerator')}
                        disabled={props.disabled}
                    />
                </Field>
            )}
            <CheckboxField
                label={l.explicitContent}
                checked={values.explicitContent}
                disabled={props.disabled}
                onChange={onCheckboxChange('explicitContent')}
                multiline
            >
                {t('age-option-adult')}
            </CheckboxField>
            <Field required label={l.logoId} error={errors.logoId} caption={t('icon-description')}>
                <Uploader
                    skillId={skill.id}
                    disabled={props.disabled}
                    url={props.logoUrl}
                    onChange={onUploaderChange}
                    imageId={values.logoId}
                    imageColor={props.logoColor}
                />
            </Field>

            {displayAppMetricaSettings && (
                <AppMetricaSettings values={values} onChange={onChange} disabled={props.disabled} />
            )}
            {displayRsyField && (
                <>
                    <Header>{t('header-advertising')}</Header>
                    <Field label={t('label-rtb-id')} description={rsyFieldDescription} error={errors.rsyPlatformId}>
                        <TextInput
                            theme="normal"
                            size="s"
                            text={values.rsyPlatformId}
                            onChange={onChange('rsyPlatformId')}
                            disabled={props.disabled}
                        />
                    </Field>
                </>
            )}

            {!props.disabled && (
                <Submit
                    text={t('button-save')}
                    inProgress={isSubmitting}
                    disabled={!isValid}
                    message={submitMessage}
                    severity={submitSeverity}
                />
            )}
        </div>
    );
};

const AliceSkillForm = formContainer<OuterProps, AliceSkillFormSettings>({
    mapPropsToValues: (props) => mapSkillSettingsToValues(props.settings),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const settings = mapValuesToSkillSettings(values);

            await updateDraft(props.skill.id, settings, Channel.AliceSkill);
            await props.refreshSnapshot();
        } catch (error) {
            await new Promise((resolve) => setErrors(createUpdateError(error, props.skill.channel), resolve));
            console.error(error);
            throw error;
        }
    },
    setupValidationSchema: (props) => {
        const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.skill.developerType);

        return {
            structuredExamples: (value: Example[], formState: FormState<AliceSkillFormSettings>) => ({
                structuredExamples: value.map((example) =>
                    validateStructuredExample(example, formState.values.activationPhrases),
                ),
            }),
            activationPhrases: (value: string[]) => ({
                activationPhrases: value.map((v) => validateActivationPhrase(v, validateAsAdmin, 'Активационное имя')),
            }),
            developerName: (value: string) => ({
                developerName: validateDeveloperName(value, validateAsAdmin, 'Имя разработчика'),
            }),
            name: (value: string) => ({
                name: value ? validateName(value, validateAsAdmin, 'Название') : 'Необходимо указать название',
            }),
            uri: (value: string) => ({
                uri: validateURL(value, props.skillUseZora),
            }),
            brandVerificationWebsite: (value: string) => ({
                brandVerificationWebsite: validateURL(value),
            }),
            description: (value: string) => ({
                description: validateDescription(value),
            }),
            noteForModerator: (value: string) => ({
                noteForModerator: validateNote(value),
            }),
            email: (value: string) => ({
                email: validateEmail(value),
            }),
            rsyPlatformId: (value: string) => ({
                rsyPlatformId: validateRsyPlatformId(value),
            }),
        };
    },
})(AliceForm);

export default withSnapshot(AliceSkillForm);
